import { useEffect } from 'react';

interface SplashScreenProps {
  onComplete: () => void;
}

// ─── Inline CSS animations ────────────────────────────────────────────────────
const CSS = `
  .sp-wrap {
    position: fixed; inset: 0; z-index: 9999;
    background: #0a0f1e;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 36px;
    animation: spIn .5s ease-out both, spOut .5s ease-in 3.0s both;
  }

  /* Stage 1 — background */
  @keyframes spIn  { from { opacity:0 } to { opacity:1 } }
  @keyframes spOut { from { opacity:1 } to { opacity:0 } }

  /* Stage 2 — rings scale in */
  #sp-rings {
    transform-box: fill-box; transform-origin: center;
    animation: spRings .7s cubic-bezier(.34,1.56,.64,1) .5s both;
  }
  @keyframes spRings {
    from { transform: scale(0); opacity: 0 }
    to   { transform: scale(1); opacity: 1 }
  }

  /* Stage 3 — circuit lines draw */
  .sp-line {
    animation: spLine .5s ease-out both;
  }
  @keyframes spLine {
    from { stroke-dashoffset: 60; opacity: 0 }
    to   { stroke-dashoffset: 0;  opacity: 0.75 }
  }

  /* Stage 4 — nodes pulse in */
  .sp-node {
    transform-box: fill-box; transform-origin: center;
    animation: spNode .4s cubic-bezier(.34,1.56,.64,1) both;
  }
  @keyframes spNode {
    0%   { transform: scale(0);   opacity: 0 }
    70%  { transform: scale(1.3);             }
    100% { transform: scale(1);   opacity: 1 }
  }

  /* Stage 5 — AI core appear + continuous glow */
  #sp-core {
    animation: spCore .5s ease-out 2.2s both;
  }
  @keyframes spCore {
    from { opacity: 0 }
    to   { opacity: 1 }
  }
  #sp-glow {
    animation: spCore .5s ease-out 2.2s both,
               spGlow 1.2s ease-in-out 2.7s infinite;
  }
  @keyframes spGlow {
    0%,100% { filter: drop-shadow(0 0 4px #00d4ff) }
    50%     { filter: drop-shadow(0 0 20px #00d4ff) drop-shadow(0 0 40px #00d4ff66) }
  }

  /* Stage 6 — brand text slide up */
  .sp-brand {
    animation: spBrand .4s ease-out 2.6s both;
  }
  @keyframes spBrand {
    from { opacity: 0; transform: translateY(16px) }
    to   { opacity: 1; transform: translateY(0) }
  }

  /* Responsive symbol size */
  .sp-svg { width: 200px; height: 200px; overflow: visible }
  @media (min-width: 768px) { .sp-svg { width: 280px; height: 280px } }

  /* Subtle scan line */
  .sp-scan {
    position: absolute; inset: 0; pointer-events: none; overflow: hidden;
  }
  .sp-scan::after {
    content: '';
    position: absolute; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(0,212,255,.12), transparent);
    animation: spScan 3.5s linear .5s both;
  }
  @keyframes spScan {
    from { top: 0 }
    to   { top: 100% }
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────
export function SplashScreen({ onComplete }: SplashScreenProps) {
  useEffect(() => {
    const t = setTimeout(() => {
      document.dispatchEvent(new Event('splashComplete'));
      onComplete();
    }, 3500);
    return () => clearTimeout(t);
  }, [onComplete]);

  return (
    <div className="sp-wrap">
      <style>{CSS}</style>
      <div className="sp-scan" />

      {/* ── SVG Symbol ── */}
      <svg className="sp-svg" viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg">

        {/* Phase 2 — Rings + hexagon + structural lines + dim nodes */}
        <g id="sp-rings">
          {/* 3 concentric rings */}
          <circle cx="200" cy="200" r="118" fill="none" stroke="#1a3a5c" strokeWidth="1.5"/>
          <circle cx="200" cy="200" r="88"  fill="none" stroke="#1a3a5c" strokeWidth="1.5"/>
          <circle cx="200" cy="200" r="58"  fill="none" stroke="#1a3a5c" strokeWidth="1.5"/>

          {/* Hexagon */}
          <polygon
            points="200,114 262,150 262,222 200,258 138,222 138,150"
            fill="none" stroke="#00d4ff" strokeWidth="1.2" opacity="0.25"
          />

          {/* 6 structural radial lines (gray) */}
          <line x1="200" y1="82"  x2="200" y2="142" stroke="#1a3a5c" strokeWidth="1.5"/>
          <line x1="200" y1="258" x2="200" y2="318" stroke="#1a3a5c" strokeWidth="1.5"/>
          <line x1="86"  y1="141" x2="138" y2="170" stroke="#1a3a5c" strokeWidth="1.5"/>
          <line x1="314" y1="141" x2="262" y2="170" stroke="#1a3a5c" strokeWidth="1.5"/>
          <line x1="86"  y1="259" x2="138" y2="230" stroke="#1a3a5c" strokeWidth="1.5"/>
          <line x1="314" y1="259" x2="262" y2="230" stroke="#1a3a5c" strokeWidth="1.5"/>

          {/* Secondary dim nodes */}
          <circle cx="200" cy="320" r="10" fill="#00d4ff" opacity="0.15"/>
          <circle cx="200" cy="320" r="4"  fill="#00d4ff" opacity="0.40"/>
          <circle cx="316" cy="261" r="10" fill="#00d4ff" opacity="0.15"/>
          <circle cx="316" cy="261" r="4"  fill="#00d4ff" opacity="0.40"/>
          <circle cx="84"  cy="139" r="10" fill="#00d4ff" opacity="0.15"/>
          <circle cx="84"  cy="139" r="4"  fill="#00d4ff" opacity="0.40"/>
        </g>

        {/* Phase 3 — 3 active cyan lines (draw animation, staggered) */}
        <line className="sp-line"
          x1="200" y1="82"  x2="200" y2="137"
          stroke="#00d4ff" strokeWidth="2"
          strokeDasharray="60 60"
          style={{ animationDelay: '1.2s' }}
        />
        <line className="sp-line"
          x1="314" y1="141" x2="266" y2="168"
          stroke="#00d4ff" strokeWidth="2"
          strokeDasharray="60 60"
          style={{ animationDelay: '1.35s' }}
        />
        <line className="sp-line"
          x1="86"  y1="259" x2="134" y2="232"
          stroke="#00d4ff" strokeWidth="2"
          strokeDasharray="60 60"
          style={{ animationDelay: '1.50s' }}
        />

        {/* Phase 4 — Primary nodes (halo + core, staggered) */}
        {/* Node 1 — top */}
        <circle className="sp-node" cx="200" cy="80"  r="14" fill="#00d4ff" opacity="0.15"
          style={{ animationDelay: '1.8s' }}/>
        <circle className="sp-node" cx="200" cy="80"  r="6"  fill="#00d4ff"
          style={{ animationDelay: '1.8s' }}/>
        {/* Node 2 — right-top */}
        <circle className="sp-node" cx="316" cy="139" r="14" fill="#00d4ff" opacity="0.15"
          style={{ animationDelay: '1.95s' }}/>
        <circle className="sp-node" cx="316" cy="139" r="6"  fill="#00d4ff"
          style={{ animationDelay: '1.95s' }}/>
        {/* Node 3 — left-bottom */}
        <circle className="sp-node" cx="84"  cy="261" r="14" fill="#00d4ff" opacity="0.15"
          style={{ animationDelay: '2.1s' }}/>
        <circle className="sp-node" cx="84"  cy="261" r="6"  fill="#00d4ff"
          style={{ animationDelay: '2.1s' }}/>

        {/* Phase 5 — AI core */}
        <g id="sp-core">
          {/* Ambient glow rings */}
          <circle cx="200" cy="200" r="44" fill="#00d4ff" opacity="0.07"/>
          <circle cx="200" cy="200" r="30" fill="#00d4ff" opacity="0.10"/>
          {/* Core + glow pulse */}
          <g id="sp-glow">
            <circle cx="200" cy="200" r="18" fill="#00d4ff" opacity="0.95"/>
            <text
              x="200" y="207"
              textAnchor="middle"
              fill="#0a0f1e"
              fontFamily="system-ui, sans-serif"
              fontSize="14"
              fontWeight="800"
            >
              AI
            </text>
          </g>
        </g>
      </svg>

      {/* Phase 6 — Brand name */}
      <div className="sp-brand" style={{ textAlign: 'center' }}>
        <div style={{
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: '30px',
          fontWeight: 700,
          letterSpacing: '0.04em',
          lineHeight: 1,
        }}>
          <span style={{ color: '#ffffff' }}>Inovei</span>
          <span style={{ color: '#00d4ff' }}>Tech</span>
        </div>
        <div style={{
          marginTop: '8px',
          fontFamily: 'system-ui, sans-serif',
          fontSize: '10px',
          fontWeight: 500,
          letterSpacing: '3.5px',
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.30)',
        }}>
          Tecnologia · IA · Inovação
        </div>
      </div>
    </div>
  );
}
